'use client';

import { useState, useEffect } from 'react';
import CompactSidebarFilter from './Compact-Sidebar-Filter';
import AddBtn from '../components/common/AddBtn';
import SmartPagination from './Smart-Pagination';

const TableSection = ({
  SIDEBAR_OPTIONS,
  dropdownOptions,
  loadOptions,
  total_data,
  data,
  data_loading,
  page,
  pages,
  setPageNumber,
  deleteBtnClicked,
  view_all_table,
  inputFields,
  PAGE_TITLE,
  PAGE_SINGLE_TITLE,
  LINK_URL,
  DataTable,

  TABLE_TITLE = 'Employees Management',
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Load saved sidebar state
  useEffect(() => {
    const savedSidebarState = localStorage.getItem(
      'employees-sidebar-collapsed'
    );
    if (savedSidebarState) {
      setSidebarCollapsed(JSON.parse(savedSidebarState));
    }
  }, []);

  // Save sidebar state to localStorage
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem(
      'employees-sidebar-collapsed',
      JSON.stringify(newState)
    );
  };

  return (
    <div className='container-fluid'>
      <div className='row'>
        {/* Collapsible Sidebar */}
        {SIDEBAR_OPTIONS && (
          <div
            className={`${
              sidebarCollapsed ? 'd-none' : 'col-xl-2 col-lg-3'
            } transition-all`}
            style={{
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <CompactSidebarFilter
              SIDEBAR_OPTIONS={SIDEBAR_OPTIONS}
              dropdown_options={dropdownOptions}
              loadOptions={loadOptions}
            />
          </div>
        )}

        {/* Main Employees Section */}
        <div
          className={`${
            sidebarCollapsed ? 'col-lg-12' : 'col-xl-10 col-lg-9'
          } transition-all`}
        >
          <div className='card shadow-sm border-0'>
            {/* Enhanced Header with Sidebar Toggle */}
            <div className='card-header bg-white border-bottom py-3'>
              <div className='d-flex align-items-center justify-content-between'>
                <div className='d-flex align-items-center'>
                  {/* Sidebar Toggle Button */}
                  <button
                    className='btn btn-outline-secondary btn-sm me-3'
                    onClick={toggleSidebar}
                    title={sidebarCollapsed ? 'Show Filters' : 'Hide Filters'}
                    style={{
                      fontSize: '12px',
                      padding: '6px 10px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <i
                      className={`fa ${
                        sidebarCollapsed ? 'fa-filter' : 'fa-times'
                      } me-1`}
                      style={{ fontSize: '11px' }}
                    ></i>
                    {sidebarCollapsed ? 'Show Filters' : 'Hide Filters'}
                  </button>
                  <h5 className='mb-0 text-dark'>{TABLE_TITLE}</h5>
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <AddBtn item={LINK_URL} title={PAGE_SINGLE_TITLE} />
                </div>
              </div>
            </div>

            {/* Employees Summary Header */}
            <div className='card-body border-bottom bg-light py-3'>
              {sidebarCollapsed && (
                <div className='mt-3'>
                  <div
                    className='alert alert-info py-2 mb-0'
                    style={{ fontSize: '12px' }}
                  >
                    <i className='fa fa-info-circle me-2'></i>
                    Sidebar is hidden - Full width view enabled for better data
                    visibility
                  </div>
                </div>
              )}
            </div>

            <div className='card-body pt-0'>
              {/* Records Count */}
              <div className='d-flex justify-content-between align-items-center mb-3 mt-3'>
                <div
                  className='text-muted'
                  style={{ fontSize: '12px', fontWeight: '500' }}
                >
                  <i
                    className='fa fa-list me-2'
                    style={{ fontSize: '11px' }}
                  ></i>
                  {total_data} records found
                  {sidebarCollapsed && (
                    <span
                      className='badge bg-info ms-2'
                      style={{ fontSize: '10px' }}
                    >
                      <i className='fa fa-expand-arrows-alt me-1'></i>
                      Full Width
                    </span>
                  )}
                </div>
              </div>

              {/* Enhanced DataTable Container */}
              <div
                className='table-container'
                style={{
                  position: 'relative',
                  overflow: 'auto',
                  maxHeight: '75vh',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                }}
              >
                {/* Enhanced DataTable with Custom Styling */}
                <div className='enhanced-datatable'>
                  <style jsx>{`
                    .enhanced-datatable .table {
                      margin-bottom: 0;
                      border: none;
                    }

                    .enhanced-datatable .table thead th {
                      background-color: #f8f9fa;
                      border-bottom: 2px solid #dee2e6;
                      font-size: 11px;
                      font-weight: 600;
                      text-transform: uppercase;
                      color: #6c757d;
                      padding: 12px 8px;
                      border-right: 1px solid #e9ecef;
                      white-space: nowrap;
                    }

                    .enhanced-datatable .table tbody td {
                      font-size: 13px;
                      padding: 12px 8px;
                      border-right: 1px solid #f8f9fa;
                      border-bottom: 1px solid #f8f9fa;
                      vertical-align: middle;
                    }

                    .enhanced-datatable .table tbody tr:hover {
                      background-color: #f8f9fa;
                    }

                    .enhanced-datatable .btn {
                      font-size: 11px;
                      padding: 4px 8px;
                      margin: 1px;
                    }

                    .enhanced-datatable .badge {
                      font-size: 10px;
                      font-weight: 500;
                    }

                    .enhanced-datatable .table tbody tr td:last-child {
                      position: sticky;
                      right: 0;
                      background-color: #ffffff;
                      border-left: 1px solid #dee2e6;
                      box-shadow: -2px 0 4px rgba(0, 0, 0, 0.05);
                      z-index: 5;
                      min-width: 120px;
                    }

                    .enhanced-datatable .table tbody tr:hover td:last-child {
                      background-color: #f8f9fa;
                    }

                    .enhanced-datatable .table thead th:last-child {
                      position: sticky;
                      right: 0;
                      background-color: #f8f9fa;
                      border-left: 1px solid #dee2e6;
                      box-shadow: -2px 0 4px rgba(0, 0, 0, 0.05);
                      z-index: 6;
                      min-width: 120px;
                    }
                  `}</style>

                  <DataTable
                    keys={view_all_table}
                    data={data}
                    field={LINK_URL}
                    page={page}
                    deleteBtnClicked={deleteBtnClicked}
                    loading={data_loading}
                    inputFields={inputFields}
                    PAGE_TITLE={PAGE_TITLE}
                    PAGE_SINGLE_TITLE={PAGE_SINGLE_TITLE}
                    style={{
                      width: '100%',
                      tableLayout: 'fixed',
                      wordWrap: 'break-word',
                    }}
                  />
                </div>
              </div>

              <SmartPagination
                page={page}
                pages={pages}
                total_count={total_data}
                setPageNumber={setPageNumber}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableSection;
